// packages/web-ui/src/router/hash-router.ts
//
// W2-S2 — pure-hash router (Arch §8.4 + ADR-5). Hand-rolled (no react-router
// dep) so the bundle stays inside NFR-BUNDLE-1 and so URLs work in webview
// iframes that block path-based routing.

import { useSyncExternalStore } from 'react';

export type HashRoute =
  | { kind: 'chat'; sessionId?: string }
  | { kind: 'kb'; entityId?: string }
  | { kind: 'files'; path?: string }
  | { kind: 'views'; viewId?: string }
  | { kind: 'settings'; section?: string }
  | { kind: 'diff'; changesetId?: string }
  | { kind: 'git' }
  | { kind: 'skills'; name?: string }
  | { kind: 'unknown'; raw: string };

export type DestinationKind = HashRoute['kind'];

const KNOWN_KINDS: ReadonlySet<DestinationKind> = new Set<DestinationKind>([
  'chat',
  'kb',
  'files',
  'views',
  'settings',
  'diff',
  'git',
  'skills',
  'unknown',
]);

/** Tracks which `unknown` raws have already been logged so we warn once per raw. */
const warnedUnknownRaws = new Set<string>();

/**
 * Strips the leading `#`/`#/` then splits on `/`. The first segment is the
 * destination kind; the remainder is the resource id (URI-decoded).
 *
 * `parseHash('')` and `parseHash('#/')` both return `{ kind: 'chat' }`.
 */
export function parseHash(hash: string): HashRoute {
  // Normalise: strip leading `#` then optional leading `/`.
  let stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  if (stripped.startsWith('/')) stripped = stripped.slice(1);
  if (stripped === '') return { kind: 'chat' };

  const slashIdx = stripped.indexOf('/');
  const head = slashIdx === -1 ? stripped : stripped.slice(0, slashIdx);
  const tail = slashIdx === -1 ? '' : stripped.slice(slashIdx + 1);

  const decoded = tail === '' ? undefined : safeDecode(tail);

  switch (head) {
    case 'chat':
      return decoded === undefined ? { kind: 'chat' } : { kind: 'chat', sessionId: decoded };
    case 'kb':
      return decoded === undefined ? { kind: 'kb' } : { kind: 'kb', entityId: decoded };
    case 'files':
      return decoded === undefined ? { kind: 'files' } : { kind: 'files', path: decoded };
    case 'views':
      return decoded === undefined ? { kind: 'views' } : { kind: 'views', viewId: decoded };
    case 'settings':
      return decoded === undefined ? { kind: 'settings' } : { kind: 'settings', section: decoded };
    case 'diff':
      return decoded === undefined ? { kind: 'diff' } : { kind: 'diff', changesetId: decoded };
    case 'git':
      return { kind: 'git' };
    case 'skills':
      return decoded === undefined ? { kind: 'skills' } : { kind: 'skills', name: decoded };
    default: {
      const raw = stripped;
      if (!warnedUnknownRaws.has(raw)) {
        warnedUnknownRaws.add(raw);
        console.warn(`[tessa-lean/web-ui] Unknown hash route: ${raw}`);
      }
      return { kind: 'unknown', raw };
    }
  }
}

/** Re-serialises a `HashRoute` to its `#/<kind>[/<id>]` form. */
export function stringifyHash(route: HashRoute): string {
  switch (route.kind) {
    case 'chat':
      return route.sessionId === undefined
        ? '#/chat'
        : `#/chat/${encodeURIComponent(route.sessionId)}`;
    case 'kb':
      return route.entityId === undefined
        ? '#/kb'
        : `#/kb/${encodeURIComponent(route.entityId)}`;
    case 'files':
      return route.path === undefined ? '#/files' : `#/files/${encodeURIComponent(route.path)}`;
    case 'views':
      return route.viewId === undefined ? '#/views' : `#/views/${encodeURIComponent(route.viewId)}`;
    case 'settings':
      return route.section === undefined
        ? '#/settings'
        : `#/settings/${encodeURIComponent(route.section)}`;
    case 'diff':
      return route.changesetId === undefined
        ? '#/diff'
        : `#/diff/${encodeURIComponent(route.changesetId)}`;
    case 'git':
      return '#/git';
    case 'skills':
      return route.name === undefined
        ? '#/skills'
        : `#/skills/${encodeURIComponent(route.name)}`;
    case 'unknown':
      return `#/${route.raw}`;
  }
}

/** Sets `window.location.hash`; the browser fires `hashchange` asynchronously (queued task). */
export function navigateTo(route: HashRoute): void {
  if (typeof window === 'undefined') return;
  window.location.hash = stringifyHash(route);
}

/** Subscription store — single hashchange listener shared across all hook consumers. */
type Listener = () => void;
const hashListeners = new Set<Listener>();
let hashListenerAttached = false;

const subscribe: (listener: Listener) => () => void = (listener) => {
  if (typeof window === 'undefined') return () => undefined;
  hashListeners.add(listener);
  if (!hashListenerAttached) {
    window.addEventListener('hashchange', notifyAll);
    hashListenerAttached = true;
  }
  return () => {
    hashListeners.delete(listener);
    if (hashListeners.size === 0 && hashListenerAttached) {
      window.removeEventListener('hashchange', notifyAll);
      hashListenerAttached = false;
    }
  };
};

function notifyAll(): void {
  hashListeners.forEach((l) => {
    l();
  });
}

const getHashSnapshot = (): string =>
  typeof window === 'undefined' ? '' : window.location.hash;
const getHashServerSnapshot = (): string => '';

/**
 * React hook returning the current parsed `HashRoute`. Subscribes to
 * `hashchange` once per process; re-renders consumers on every navigation.
 */
export function useHashRoute(): HashRoute {
  const hash = useSyncExternalStore(subscribe, getHashSnapshot, getHashServerSnapshot);
  return parseHash(hash);
}

/** Test-only helper — clears the warn-once memory so unit tests can re-assert. */
export function _resetUnknownRouteWarnCache(): void {
  warnedUnknownRaws.clear();
}

/** Test-only helper — exposed for round-trip assertions. */
export const _KNOWN_KINDS = KNOWN_KINDS;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // Malformed URI — return the raw segment rather than throwing.
    return value;
  }
}
